// ResetPassword — page-2 of password reset flow.
// Per W2 §4.5: consumes the token from `?token=X`, applies new password,
// auto-login via the AuthResponse payload.

import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/AuthShell";
import { FormErrorBanner, FormField } from "@/components/auth/FormField";
import { Button } from "@/components/ui/button";
import { resetPasswordSchema } from "@/lib/validation/auth";
import { ApiError } from "@/lib/api/pair";
import { resetPassword } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth/AuthProvider";
import { AUTH_I18N } from "@/lib/i18n/auth";

export function ResetPasswordPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    token?: string;
    password?: string;
    confirm?: string;
  }>({});

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    const parsed = resetPasswordSchema.safeParse({
      token,
      password,
      confirm,
    });
    if (!parsed.success) {
      const errs: typeof fieldErrors = {};
      for (const i of parsed.error.issues) {
        if (i.path[0] === "token") errs.token = i.message;
        if (i.path[0] === "password") errs.password = i.message;
        if (i.path[0] === "confirm") errs.confirm = i.message;
      }
      setFieldErrors(errs);
      return;
    }
    setBusy(true);
    try {
      const result = await resetPassword({
        token: parsed.data.token,
        password: parsed.data.password,
      });
      await refresh();
      nav("/dashboard", { replace: true });
      return result;
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "AUTH_INVALID_RESET_TOKEN") {
          setFormError(AUTH_I18N.password_reset_failed);
        } else {
          setFormError(err.message);
        }
      } else {
        setFormError("重設時發生錯誤, 請稍後再試");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="重設密碼"
      subtitle="輸入新密碼後, 我們會自動登入。"
      backHref="/login"
      footer={
        <Link to="/login" className="text-you-strong font-medium" data-testid="link-login">
          回到登入
        </Link>
      }
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <FormErrorBanner message={formError} />
        <FormField
          name="password"
          label="新密碼"
          type="password"
          required
          autoComplete="new-password"
          minLength={8}
          maxLength={128}
          value={password}
          onChange={setPassword}
          invalid={Boolean(fieldErrors.password)}
          errorMessage={fieldErrors.password}
          disabled={busy}
          autoFocus
          data-testid="field-password"
        />
        <FormField
          name="confirm"
          label="再次輸入新密碼"
          type="password"
          required
          autoComplete="new-password"
          value={confirm}
          onChange={setConfirm}
          invalid={Boolean(fieldErrors.confirm)}
          errorMessage={fieldErrors.confirm}
          disabled={busy}
          data-testid="field-confirm"
        />
        <Button
          type="submit"
          variant="primary"
          disabled={busy || !token}
          data-testid="submit-reset"
          leadingIcon={busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        >
          {busy ? "送出中…" : "重設並登入"}
        </Button>
      </form>
    </AuthShell>
  );
}
